import { DEFAULT_LIMIT } from "../constants/important";
import { IQuery } from "../components/organisms/Menus/QueryMenu/QueryMenu";

import { COUNTRY } from "constants/country";
import { CATEGORIES } from "constants/categories";

export function QueryStringBuilder(category: string, subcategories?: string[]) {
  const base = `?category=${category}`;
  if (subcategories) {
    return `${base}&subcategory=${subcategories.join(",")}`;
  }
  return base;
}

export function SearchQueryBuilder(
  filter: string,
  offset: number,
  type?: string,
  createdAt?: string,
) {
  let base = `?query=${filter}&limit=${DEFAULT_LIMIT}&offset=${offset}`;
  if (type && createdAt) {
    base += `&type=${type}&createdAt=${createdAt}`;
  }
  return base;
}

export const getCountQuery = () => {
  // to be implemented
  const countQuery = "";
  return countQuery;
};

export function QueryBuilder(
  query: IQuery,
  queryType: "query" | "filter" | "wide" | "count" = "filter",
) {
  const {
    limit,
    offset,
    queryValue,
    category,
    ignoredCategory,
    keywords,
    countries,
    fromDate,
    toDate,
    id,
    show,
    highlighted,
    highlightedInCountry,
    type,
    title,
    displayInHomePage,
    partner,
    diasporeMedia,
    homepageRank,
    sort,
    author,
    regions,
    exclusive,
  } = query;
  if (queryValue && queryType === "query") {
    return `?query=${queryValue}&limit=${limit}&offset=${offset}&sort=createdAt:desc,toBePostedAt:desc`;
  }
  const criteria: string[] = [];
  if (fromDate) {
    const fromDateCriteria = `fromDate=${fromDate}`;
    criteria.push(fromDateCriteria);
  }
  if (toDate) {
    const toDateCriteria = `toDate=${toDate}`;
    criteria.push(toDateCriteria);
  }
  if (id) {
    const organizationCriteria = `id=${id}`;
    criteria.push(organizationCriteria);
  }
  if (show) {
    const showCriteria = `show=${show}`;
    criteria.push(showCriteria);
  }
  if (partner) {
    const partnerCriteria = `partner=${partner}`;
    criteria.push(partnerCriteria);
  }
  if (exclusive) {
    const exclusiveCriteria = `exclusive=${exclusive}`;
    criteria.push(exclusiveCriteria);
  }
  if (diasporeMedia) {
    const diasporeMediaCriteria = `diasporeMedia=${diasporeMedia}`;
    criteria.push(diasporeMediaCriteria);
  }
  if (author && author.length > 0) {
    const authorCriteria = `author=${author.join(",")}`;
    criteria.push(authorCriteria);
  }
  if (category && category.length > 0) {
    const categoryCriteria = `category=${category.join(",")}`;
    criteria.push(categoryCriteria);
  }
  if (regions) {
    const categoryCriteria = `region=${regions.join(",")}`;
    criteria.push(categoryCriteria);
  }
  if (ignoredCategory && ignoredCategory?.length > 0) {
    const ignoredCategoryCriteria = `ignoredCategory=${ignoredCategory.join(
      ",",
    )}`;
    criteria.push(ignoredCategoryCriteria);
  }
  if (keywords && keywords.length > 0) {
    const keywordsCriteria = `keywords=${keywords.join(",")}`;
    criteria.push(keywordsCriteria);
  }
  if (countries && countries.length > 0) {
    const countriesCriteria = `countries=${countries.join(",")}`;
    criteria.push(countriesCriteria);
  }
  if (highlighted) {
    const highlightedCriteria = `highlighted=${highlighted}`;
    criteria.push(highlightedCriteria);
  }
  if (highlightedInCountry) {
    const highlightedInCountryCriteria = `highlightedInCountry=${highlightedInCountry}`;
    criteria.push(highlightedInCountryCriteria);
  }
  if (displayInHomePage === "true") {
    const displayCriteria = `displayInHomePage`;
    criteria.push(displayCriteria);
  }
  if (queryValue) {
    const queryCriteria = `query=${queryValue}`;
    criteria.push(queryCriteria);
  }
  if (type && type.length > 0) {
    const queryCriteria = `type=${type.join(",")}`;
    criteria.push(queryCriteria);
  }
  if (title) {
    const titleCriteria = `title=${title}`;
    criteria.push(titleCriteria);
  }
  if (sort) {
    const keys = Object.keys(sort);
    if (keys.length) {
      const sorts: string[] = [];
      keys.forEach((key) => {
        sorts.push(`${key}${sort[key] === "desc" ? ":desc" : ""}`);
      });
      const sortCriteria = `sort=${sorts.join(",")}`;
      criteria.push(sortCriteria);
    }
  }
  if (homepageRank) {
    const queryCriteria = `homepageRank=${homepageRank.join(",")}`;
    criteria.push(queryCriteria);
  }
  const queryCriteria = criteria.join("&");
  if (queryType === "wide") {
    return `?${queryCriteria}&limit=${limit}&offset=${offset}`;
  }
  return `/${queryType}?${queryCriteria}&limit=${limit}&offset=${offset}`;
}

export function ParseQueryValues(attributes: string[]) {
  const keys: Record<string, any> = {};
  attributes.forEach((att) => {
    const keyValuePair = att.split("=");
    const key = keyValuePair[0];
    const value = keyValuePair[1];
    keys[key] = value;
  });

  const query: IQuery = {
    queryValue: keys.query,
    regions: keys.region?.toString().split(","),
    type: keys.type?.toString().split(","),
    category: keys.category?.toString().split(","),
    keywords: keys.keywords?.toString().split(","),
    countries: keys.countries?.toString().split(","),
    title: keys.title,
    highlighted: keys.highlighted,
    highlightedInCountry: keys.highlightedInCountry,
    fromDate: keys.fromDate,
    toDate: keys.toDate,
    show: keys.show,
    id: keys.id,
    limit: Number(keys.limit || DEFAULT_LIMIT),
    offset: Number(keys.offset) || 0,
    lng: keys.lng,
    showAllRecords: keys.showAllRecords,
  };
  return query;
}

export const EmptyQuery: IQuery = {
  queryValue: "",
  category: [],
  keywords: [],
  countries: [],
  fromDate: "",
  toDate: "",
  limit: DEFAULT_LIMIT,
  offset: 0,
  type: [],
};

export function queryStringBuilder(filters?: IIFrameFilter) {
  if (filters) {
    const f: string[] = [];
    Object.entries(filters).forEach(([filterKey, filterValues]) => {
      const key = filterKey as string;
      const values = filterValues as string[];
      if (values && Array.isArray(values) && values.length > 0)
        f.push(`${key}=${values.join(",")}`);
      else f.push(`${key}=${values}`);
    });
    return f.length ? `?${f.join("&")}` : "";
  }

  return "";
}
interface IIFrameFilter {
  limit: number;
  offset: number;
  countries?: COUNTRY[];
  category?: CATEGORIES[];
  type?: string;
  lng?: string;
  highlighted?: "true" | "false";
  highlightedInCountry?: "true" | "false";
  displayInHomePage?: "true" | "false";
}

export const MapIframeFieldsToFilter = (state: Record<string, any>) => {
  const newFilter: IIFrameFilter = {
    limit: 12,
    offset: 0,
  };
  Object.keys(state).forEach((key) => {
    const value = state[key];
    if (key === "country" && value.length > 0) newFilter.countries = value;
    else if (key === "type") newFilter.type = value;
    else if (key === "amount") newFilter.limit = value;
    else if (key === "category" && value.length > 0) newFilter.category = value;
    else if (key === "special") {
      if (value === "izcelts") newFilter.highlighted = "true";
      else if (value === "izcelts-sakumlapa") {
        newFilter.highlighted = "true";
        newFilter.displayInHomePage = "true";
      } else if (value === "radit-sakumlapa")
        newFilter.displayInHomePage = "true";
      else if (value === "izcelts-valsti")
        newFilter.highlightedInCountry = "true";
    } else if (key === "language") newFilter.lng = value;
  });

  const query = queryStringBuilder(newFilter);

  return `${query}`;
};
